import z from "zod";

const openPhoneErrorTypeValues = [
  "INVALID_WEBHOOK_TYPE",
  "PARTICIPANT_NOT_FOUND",
  "FEATURE_NOT_IMPLEMENTED",
  "SCHEMA_VALIDATION_FAILED",
  "CALL_TRANSCRIPT_PARSE_FAILED",
  "INVALID_WEBHOOK_OBJECT_TYPE",
  "INVALID_WEBHOOK_SUMMARY_TYPE",
  "INVALID_WEBHOOK_API_VERSION",
  "DEV_NUMBER_IN_PRODUCTION",
  "NO_EXTERNAL_PARTICIPANTS",
  "WEBHOOK_AUTH_FAILED",
  "SMS_BODY_REQUIRED",
  "SMS_SEND_FAILED",
  "PHONE_NUMBER_NOT_FOUND",
] as const;

const openPhoneErrorTypeSchema = z.enum(openPhoneErrorTypeValues);
const OpenPhoneErrorType = openPhoneErrorTypeSchema.enum;
type OpenPhoneErrorType = z.infer<typeof openPhoneErrorTypeSchema>;

export type OpenPhoneErrors =
  | {
      errorType: typeof OpenPhoneErrorType.INVALID_WEBHOOK_TYPE;
      data: { receivedType: string; expectedTypes: readonly string[]; category: "call" | "message" | "summary" };
    }
  | {
      errorType: typeof OpenPhoneErrorType.PARTICIPANT_NOT_FOUND;
      data: { participantType: "from" | "external"; incomingCall: boolean; participantCount: number };
    }
  | { errorType: typeof OpenPhoneErrorType.FEATURE_NOT_IMPLEMENTED; data: { featureName: string } }
  | {
      errorType: typeof OpenPhoneErrorType.SCHEMA_VALIDATION_FAILED;
      data: { inputValue: string; validationType: string };
    }
  | {
      errorType: typeof OpenPhoneErrorType.CALL_TRANSCRIPT_PARSE_FAILED;
      data: { attemptedSchemas: readonly string[]; callId?: string };
    }
  | {
      errorType: typeof OpenPhoneErrorType.INVALID_WEBHOOK_OBJECT_TYPE;
      data: { receivedObjectType: string; expectedObjectType: string; webhookCategory: "call" | "message" | "summary" };
    }
  | {
      errorType: typeof OpenPhoneErrorType.INVALID_WEBHOOK_SUMMARY_TYPE;
      data: { receivedSummaryType: string; expectedTypes: readonly string[] };
    }
  | {
      errorType: typeof OpenPhoneErrorType.INVALID_WEBHOOK_API_VERSION;
      data: { receivedApiVersion: string; expectedApiVersion: string };
    }
  | {
      errorType: typeof OpenPhoneErrorType.DEV_NUMBER_IN_PRODUCTION;
      data: { devNumber: string; participants: readonly string[] };
    }
  | {
      errorType: typeof OpenPhoneErrorType.NO_EXTERNAL_PARTICIPANTS;
      data: { participantCount: number; openPhoneParticipants: readonly string[] };
    }
  | {
      errorType: typeof OpenPhoneErrorType.WEBHOOK_AUTH_FAILED;
      data: {
        reason: "missing_signature" | "invalid_signature_format" | "signature_mismatch" | "not_string";
        providedSignature?: string;
      };
    }
  | {
      errorType: typeof OpenPhoneErrorType.SMS_BODY_REQUIRED;
      data: { from: string; to: string };
    }
  | {
      errorType: typeof OpenPhoneErrorType.SMS_SEND_FAILED;
      data: { from: string; to: string; body?: string; failedPhoneId?: string };
    }
  | {
      errorType: typeof OpenPhoneErrorType.PHONE_NUMBER_NOT_FOUND;
      data: { openPhoneNumberId: string; availableNumbers?: readonly string[] };
    };

export const openPhoneErrorDetails: Record<OpenPhoneErrorType, { sentryDescription: string }> = {
  INVALID_WEBHOOK_TYPE: { sentryDescription: "Received invalid OpenPhone webhook type" },
  PARTICIPANT_NOT_FOUND: { sentryDescription: "Required participant not found in OpenPhone call" },
  FEATURE_NOT_IMPLEMENTED: { sentryDescription: "OpenPhone feature not implemented" },
  SCHEMA_VALIDATION_FAILED: { sentryDescription: "OpenPhone schema validation failed" },
  CALL_TRANSCRIPT_PARSE_FAILED: { sentryDescription: "Failed to parse OpenPhone call transcript webhook" },
  INVALID_WEBHOOK_OBJECT_TYPE: { sentryDescription: "OpenPhone webhook contains invalid object type" },
  INVALID_WEBHOOK_SUMMARY_TYPE: { sentryDescription: "OpenPhone call summary webhook has invalid type" },
  INVALID_WEBHOOK_API_VERSION: { sentryDescription: "OpenPhone webhook has unsupported API version" },
  DEV_NUMBER_IN_PRODUCTION: { sentryDescription: "Development phone number used in production environment" },
  NO_EXTERNAL_PARTICIPANTS: { sentryDescription: "No external participants found in OpenPhone call" },
  WEBHOOK_AUTH_FAILED: { sentryDescription: "OpenPhone webhook authentication failed" },
  SMS_BODY_REQUIRED: { sentryDescription: "SMS body is required for OpenPhone message" },
  SMS_SEND_FAILED: { sentryDescription: "Failed to send SMS via OpenPhone" },
  PHONE_NUMBER_NOT_FOUND: { sentryDescription: "OpenPhone number not found" },
};
